import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { useLoad } from "@redotech/react-util/load";
import { RedoMarketplaceRpcClientContext } from "@redotech/redo-marketplace-app-common/rpc-client";
import { VendorAuthContext } from "@redotech/redo-marketplace-app-common/vendor-auth";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoSpinner } from "@redotech/redo-web/arbiter-components/loading/redo-spinner";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useContext } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ProductDetails } from "./components/product-details/product-details";

export const ProductDetailPage = memo(function ProductDetailPage() {
  const { productId } = useParams<{ productId: string }>();
  const navigate = useNavigate();
  const vendorAuth = useContext(VendorAuthContext);
  const marketplaceRpcClient = useRequiredContext(
    RedoMarketplaceRpcClientContext,
  );

  const productResult = useLoad(async () => {
    if (!vendorAuth || !productId) {
      return null;
    }
    return await marketplaceRpcClient.getProductDetails({
      listingId: productId,
    });
  }, [marketplaceRpcClient, vendorAuth, productId]);

  const handleBackToProducts = useHandler(() => {
    navigate("/");
  });

  const handleSignIn = useHandler(() => {
    navigate("/login");
  });

  if (!productId) {
    return (
      <Flex
        align="center"
        dir="column"
        gap="lg"
        justify="center"
        p="xl"
        style={{ minHeight: "400px" }}
      >
        <Text color="red" fontSize="xl" fontWeight="semibold">
          Invalid product URL
        </Text>
        <RedoButton
          hierarchy="primary"
          onClick={handleBackToProducts}
          size="lg"
          text="Back to Marketplace"
        />
      </Flex>
    );
  }

  return (
    <main className="flex-1 w-full h-full">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 py-4 h-full">
        {!vendorAuth ? (
          <Flex
            align="center"
            dir="column"
            gap="lg"
            justify="center"
            p="xl"
            style={{ minHeight: "400px" }}
          >
            <Text color="gray" fontSize="xl" fontWeight="semibold">
              🛍️ Welcome to Redo Marketplace
            </Text>
            <Text color="gray" fontSize="md" textAlign="center">
              Sign in to browse and purchase products from our marketplace
            </Text>
            <RedoButton
              hierarchy="primary"
              onClick={handleSignIn}
              size="lg"
              text="Sign In to Browse Marketplace"
            />
          </Flex>
        ) : (
          <Flex align="center" flexDirection="column" w="full">
            {productResult.pending && <RedoSpinner />}

            {productResult.error && (
              <Flex
                align="center"
                dir="column"
                gap="lg"
                p="xl"
                style={{ minHeight: "400px" }}
              >
                <Text color="red">
                  Error loading product: {productResult.error.message}
                </Text>
                <RedoButton
                  hierarchy="primary"
                  onClick={handleBackToProducts}
                  size="lg"
                  text="Back to Marketplace"
                />
              </Flex>
            )}

            {productResult.value && (
              <ProductDetails product={productResult.value} />
            )}
          </Flex>
        )}
      </div>
    </main>
  );
});
